import { useEffect, useMemo } from 'react'
import { Award, Trophy } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageHeader } from '@/components/layout'
import {
  AchievementGrid,
  LevelProgress,
  StreakIndicator,
} from '@/components/gamification'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { visitPage } from '@/store/slices/gamificationSlice'
import type { AchievementCategory } from '@/types'
import { getAchievementStats, getAchievementsByCategory } from '@/lib/achievements'

export default function Achievements() {
  const dispatch = useAppDispatch()
  const gamification = useAppSelector(state => state.gamification)

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('achievements'))
  }, [dispatch])

  const stats = useMemo(() => 
    getAchievementStats(gamification.achievements),
    [gamification.achievements]
  )

  const categoryStats = useMemo(() => 
    getAchievementsByCategory(gamification.achievements),
    [gamification.achievements]
  )

  const categories: { id: AchievementCategory | 'all'; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'goals', label: 'Goals' },
    { id: 'savings', label: 'Savings' },
    { id: 'streaks', label: 'Streaks' },
    { id: 'milestones', label: 'Milestones' },
    { id: 'engagement', label: 'Engagement' },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Achievements"
        description="Track your progress and unlock rewards"
      />

      {/* Profile Stats */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Level & XP Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Your Progress
            </CardTitle>
            <CardDescription>
              Level up by completing goals and earning achievements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LevelProgress
              level={gamification.level}
              totalXP={gamification.totalXP}
              variant="full"
            />
          </CardContent>
        </Card>

        {/* Streak Card */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Streak</CardTitle>
            <CardDescription>
              Keep the momentum going!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StreakIndicator
              currentStreak={gamification.currentStreak}
              longestStreak={gamification.longestStreak}
              lastActivityDate={gamification.lastActivityDate}
              variant="full"
            />
          </CardContent>
        </Card>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.unlocked}</p>
              <p className="text-sm text-muted-foreground">Unlocked</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.earnedPoints}</p>
              <p className="text-sm text-muted-foreground">XP Earned</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {Math.round((stats.unlocked / stats.total) * 100)}%
              </p>
              <p className="text-sm text-muted-foreground">Complete</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Achievements Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            All Achievements
          </CardTitle>
          <CardDescription>
            {stats.unlocked} of {stats.total} achievements unlocked
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList className="mb-6">
              {categories.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id}>
                  {cat.label}
                  {cat.id !== 'all' && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({categoryStats[cat.id as AchievementCategory]?.unlocked || 0}/
                      {categoryStats[cat.id as AchievementCategory]?.total || 0})
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.id}>
                <AchievementGrid
                  achievements={gamification.achievements}
                  category={cat.id}
                  showLocked={true}
                  size="md"
                />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {Object.entries(categoryStats).map(([category, data]) => (
          <Card key={category}>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm font-medium capitalize mb-1">{category}</p>
                <p className="text-2xl font-bold">
                  {data.unlocked}/{data.total}
                </p>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(data.unlocked / data.total) * 100}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
